import { jsx, Fragment, jsxs } from "react/jsx-runtime";
import { Box, IconChevronUp, IconChevronDown, Divider, mediaBreakpointMixin, IconButton, IconChevronDownOutline } from "@zillow/constellation";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useDropdownBehavior } from "../../hooks/useDropdownBehavior.esm.js";
import { GROUP_WIDTH_MAP } from "../../mixins/components/GroupMixin.esm.js";
import { parseSubnavItems } from "../../utils/parseSubnavItems.esm.js";
import SearchBox from "./SearchBox.esm.js";
import SubnavDropdown from "./SubnavDropdown.esm.js";
const StyledDivider = styled(Divider).withConfig({
  displayName: "B2BSubnav__StyledDivider",
  componentId: "sc-mir6ll-0"
})(["@media ", "{display:none;}"], mediaBreakpointMixin("lg_lte"));
const StyledDropdownButton = styled(IconButton).withConfig({
  displayName: "B2BSubnav__StyledDropdownButton",
  componentId: "sc-mir6ll-1"
})(["margin-top:2px;@media ", "{display:none;}"], mediaBreakpointMixin("xl_gte"));
const SUBNAV_HEIGHT_PX = 58;
const SubnavLink = ({
  url,
  title,
  home,
  inDropdown = false,
  styles
}) => {
  const getColor = () => {
    if (home) return "$colors.textDark";
    if (inDropdown) return "$colors.brand";
    return "$colors.textMedium";
  };
  const commonStyles = {
    textDecoration: "none",
    fontWeight: home ? "$fontWeights.bold" : "$fontWeights.body",
    fontSize: "$fontSizes.bodySmall",
    fontFamily: "$fonts.sansSerif",
    color: getColor(),
    "@media $xl": {
      padding: "$spacing.xs $spacing.sm"
    },
    ...styles
  };
  if (!url || url.trim() === "") {
    return /* @__PURE__ */ jsx(Box, { sx: {
      ...commonStyles,
      cursor: "default"
    }, children: title });
  }
  return /* @__PURE__ */ jsx(Box, { as: "a", href: url, sx: {
    ...commonStyles,
    ":hover": {
      textDecoration: "underline",
      color: "$colors.blue500"
    }
  }, dangerouslySetInnerHTML: {
    __html: title
  } });
};
const SubnavItemWithDropdown = ({
  item
}) => {
  const {
    dropdown,
    ref,
    onMouseEnter,
    onMouseLeave
  } = useDropdownBehavior();
  const hasChildren = item.children && item.children.length > 0;
  if (hasChildren) {
    return /* @__PURE__ */ jsxs(Box, { ref, sx: {
      position: "relative"
    }, onMouseEnter, onMouseLeave, children: [
      /* @__PURE__ */ jsxs(Box, { sx: {
        display: "flex",
        alignItems: "center"
      }, children: [
        /* @__PURE__ */ jsx(SubnavLink, { url: item.url, title: item.title, styles: {
          "@media $xl": {
            padding: "$spacing.xs $spacing.sm",
            paddingRight: "$spacing.none"
          }
        } }),
        /* @__PURE__ */ jsx(Box, { as: IconButton, bare: true, size: "xs", "aria-haspopup": "menu", "aria-expanded": dropdown ? "true" : "false", "aria-label": dropdown ? `Close ${item.title} menu` : `Open ${item.title} menu`, icon: /* @__PURE__ */ jsx(IconChevronDownOutline, {}), sx: {
          "&&&:hover": {
            backgroundColor: "transparent"
          }
        } })
      ] }),
      /* @__PURE__ */ jsx(SubnavDropdown, { isOpen: dropdown, renderLink: (childItem) => /* @__PURE__ */ jsx(SubnavLink, { url: childItem.url, title: childItem.title, inDropdown: true }), children: item.children || [] })
    ] });
  }
  return /* @__PURE__ */ jsx(SubnavLink, { url: item.url, title: item.title });
};
const MobileSubnavItem = ({
  item,
  isNested = false
}) => {
  var _a;
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const fontSize = "$fontSizes.body";
  if (hasChildren) {
    return /* @__PURE__ */ jsxs(Box, { sx: {
      // Only add border-bottom for top-level items, not nested children
      ...!isNested && {
        borderBottom: "solid 1px $colors.borderLight"
      },
      width: "100%"
    }, children: [
      /* @__PURE__ */ jsxs(Box, { sx: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        // Nested items use smaller padding to match B2BMainNav DropdownMenuItem
        padding: isNested ? "$spacing.xs $spacing.sm" : "$spacing.sm"
      }, children: [
        /* @__PURE__ */ jsx(Box, { as: "a", href: item.url, "aria-haspopup": "menu", "aria-expanded": isOpen ? "true" : "false", sx: {
          display: "flex",
          alignItems: "center",
          flex: 1,
          textDecoration: "none",
          fontWeight: "$fontWeights.body",
          fontSize,
          fontFamily: "$fonts.sansSerif",
          // Match B2BMainNav TextButton color for nested children
          color: isNested ? "$colors.brand" : "$colors.textMedium",
          ":hover, :focus": {
            color: "$colors.brand"
          }
        }, children: item.title }),
        /* @__PURE__ */ jsx(Box, { as: IconButton, bare: true, size: "xs", "aria-haspopup": "menu", "aria-expanded": isOpen ? "true" : "false", "aria-label": isOpen ? `Close ${item.title} menu` : `Open ${item.title} menu`, icon: /* @__PURE__ */ jsx(IconChevronDownOutline, {}), onClick: () => setIsOpen(!isOpen), sx: {
          "&&&:hover": {
            backgroundColor: "transparent"
          }
        } })
      ] }),
      isOpen && /* @__PURE__ */ jsx(Box, { as: "ul", sx: {
        backgroundColor: "$colors.backgroundLight",
        borderTop: "solid 1px $colors.borderLight",
        padding: "$spacing.xs 0",
        position: "relative",
        width: "100%",
        listStyle: "none",
        margin: "$spacing.none",
        paddingInlineStart: "$spacing.none"
      }, children: (_a = item.children) == null ? void 0 : _a.map((child) => /* @__PURE__ */ jsx(Box, { as: "li", sx: {
        fontFamily: "$fonts.sansSerif",
        lineHeight: "$lineHeights.body"
      }, children: /* @__PURE__ */ jsx(MobileSubnavItem, { item: child, isNested: true }) }, child.id)) })
    ] });
  }
  return /* @__PURE__ */ jsx(Box, { sx: {
    // Only add border-bottom for top-level items, not nested children
    ...!isNested && {
      borderBottom: "solid 1px $colors.borderLight"
    },
    width: "100%"
  }, children: /* @__PURE__ */ jsx(Box, { as: "a", href: item.url, sx: {
    display: "block",
    // Nested items use smaller padding to match B2BMainNav DropdownMenuItem
    padding: isNested ? "$spacing.xs $spacing.sm" : "$spacing.sm",
    textDecoration: "none",
    fontWeight: "$fontWeights.body",
    fontSize,
    fontFamily: "$fonts.sansSerif",
    lineHeight: "$lineHeights.body",
    color: isNested ? "$colors.brand" : "$colors.textMedium",
    ":hover, :focus": {
      color: "$colors.brand",
      backgroundColor: "$colors.backgroundBlue"
    }
  }, children: item.title }) });
};
const updateTopOffset = ({
  subnavRef,
  setTopOffset
}) => {
  if (!subnavRef.current) return;
  const parent = subnavRef.current.offsetParent;
  if (parent) {
    const parentRect = parent.getBoundingClientRect();
    const subnavRect = subnavRef.current.getBoundingClientRect();
    setTopOffset(subnavRect.top - parentRect.top);
  } else {
    const rect = subnavRef.current.getBoundingClientRect();
    setTopOffset(rect.top);
  }
};
const B2BSubnav = ({
  menu,
  primaryItem: primaryItemProp,
  items: itemsProp = [],
  searchPrefix: searchPrefixProp,
  searchPlaceholder: searchPlaceholderProp,
  isOpen: controlledIsOpen,
  onOpenChange
}) => {
  var _a, _b, _c;
  const items = menu ? parseSubnavItems(menu.items) : itemsProp;
  const primaryItem = ((_a = menu == null ? void 0 : menu.meta) == null ? void 0 : _a.title) && ((_b = menu == null ? void 0 : menu.meta) == null ? void 0 : _b.url) ? {
    id: 0,
    title: menu.meta.title,
    url: menu.meta.url,
    parent: 0
  } : primaryItemProp;
  const searchPrefix = ((_c = menu == null ? void 0 : menu.meta) == null ? void 0 : _c.search) || searchPrefixProp;
  const searchPlaceholder = searchPlaceholderProp;
  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const isControlled = controlledIsOpen !== void 0;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const [topOffset, setTopOffset] = React.useState(0);
  const subnavRef = React.useRef(null);
  const prevIsControlledRef = React.useRef(isControlled);
  const lastControlledValueRef = React.useRef(controlledIsOpen);
  useEffect(() => {
    const wasControlled = prevIsControlledRef.current;
    const isNowControlled = isControlled;
    if (wasControlled && !isNowControlled) {
      setInternalIsOpen(lastControlledValueRef.current ?? false);
    }
    if (isControlled && controlledIsOpen !== void 0) {
      lastControlledValueRef.current = controlledIsOpen;
    }
    prevIsControlledRef.current = isControlled;
  }, [isControlled, controlledIsOpen]);
  useEffect(() => {
    if (isControlled && controlledIsOpen) {
      updateTopOffset({
        subnavRef,
        setTopOffset
      });
    }
  }, [controlledIsOpen, isControlled]);
  useEffect(() => {
    updateTopOffset({
      subnavRef,
      setTopOffset
    });
  }, []);
  useEffect(() => {
    if (isOpen) return;
    const updateOffset = () => {
      updateTopOffset({
        subnavRef,
        setTopOffset
      });
    };
    window.addEventListener("resize", updateOffset);
    window.addEventListener("scroll", updateOffset);
    return () => {
      window.removeEventListener("resize", updateOffset);
      window.removeEventListener("scroll", updateOffset);
    };
  }, [isOpen]);
  const handleToggle = () => {
    const newIsOpen = !isOpen;
    if (newIsOpen) {
      updateTopOffset({
        subnavRef,
        setTopOffset
      });
    }
    if (isControlled) {
      onOpenChange == null ? void 0 : onOpenChange(newIsOpen);
    } else {
      setInternalIsOpen(newIsOpen);
    }
  };
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  if (!items.length) {
    return null;
  }
  return /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsx(Box, { ref: subnavRef, "data-testid": "b2b-subnav", sx: {
    borderBottom: "1px solid $colors.borderLight",
    "@media $lg_lte": {
      ...isOpen && {
        position: "absolute",
        top: `${topOffset}px`,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: `calc(100dvh - ${topOffset}px)`,
        minHeight: `calc(100dvh - ${topOffset}px)`,
        zIndex: "100",
        backgroundColor: "$colors.backgroundWhite"
      }
    }
  }, children: /* @__PURE__ */ jsxs(Box, { sx: {
    display: "flex",
    flexDirection: "column",
    "@media $lg_lte": {
      ...isOpen && {
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        paddingBottom: "$spacing.xl"
      }
    }
  }, children: [
    /* @__PURE__ */ jsxs(Box, { sx: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      height: `${SUBNAV_HEIGHT_PX}px`,
      minHeight: `${SUBNAV_HEIGHT_PX}px`,
      position: "relative",
      padding: "0 $spacing.sm",
      maxWidth: GROUP_WIDTH_MAP.xl,
      margin: "0 auto",
      width: "100%"
    }, children: [
      /* @__PURE__ */ jsxs(Box, { sx: {
        display: "flex",
        alignItems: "stretch",
        "@media $lg_lte": {
          alignItems: "center"
        }
      }, children: [
        primaryItem && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Box, { sx: {
            display: "flex",
            alignItems: "center"
          }, children: /* @__PURE__ */ jsx(SubnavLink, { url: primaryItem.url, title: primaryItem.title, home: true }) }),
          /* @__PURE__ */ jsx(StyledDivider, { orientation: "vertical", marginX: "sm" })
        ] }),
        /* @__PURE__ */ jsx(StyledDropdownButton, { buttonType: "tertiary", bare: true, size: "xs", "aria-expanded": isOpen ? "true" : "false", icon: isOpen ? /* @__PURE__ */ jsx(IconChevronUp, {}) : /* @__PURE__ */ jsx(IconChevronDown, {}), title: "Open menu", onClick: handleToggle }),
        /* @__PURE__ */ jsx(Box, { sx: {
          display: "flex",
          alignItems: "center",
          "@media $lg_lte": {
            display: "none"
          }
        }, children: items.map((item) => /* @__PURE__ */ jsx(SubnavItemWithDropdown, { item }, item.id)) })
      ] }),
      searchPrefix && /* @__PURE__ */ jsx(SearchBox, { placeholder: searchPlaceholder ? searchPlaceholder : "Search", actionPrefix: searchPrefix })
    ] }),
    /* @__PURE__ */ jsx(Box, { as: "ul", sx: {
      display: isOpen ? "block" : "none",
      listStyle: "none",
      padding: "$spacing.none",
      margin: "$spacing.none",
      width: "100%",
      "@media $xl_gte": {
        display: "none"
      }
    }, children: items.map((item) => /* @__PURE__ */ jsx(Box, { as: "li", children: /* @__PURE__ */ jsx(MobileSubnavItem, { item }) }, item.id)) })
  ] }) }) });
};
export {
  B2BSubnav as default
};
